/**
 * «Что делает Claude» — живая лента шагов задачи: поиски, прочитанные страницы со ссылками,
 * короткие мысли, генерация картинок, итог и время.
 */
import { useEffect, useState } from "react";
import {
  Check,
  ChevronDown,
  CircleDot,
  FileText,
  ImageIcon,
  Lightbulb,
  PenLine,
  Search,
  TriangleAlert,
  Wrench,
  type LucideIcon,
} from "lucide-react";
import type { JobEvent, JobEventKind, JobSummary } from "@impuls/shared";
import { domainOf, duration } from "../lib/format";
import { EcgLoader } from "./Ecg";

const ICON: Record<JobEventKind, LucideIcon> = {
  status: CircleDot,
  thinking: Lightbulb,
  text: PenLine,
  search: Search,
  fetch: FileText,
  tool: Wrench,
  image: ImageIcon,
  error: TriangleAlert,
  done: Check,
};

/** Секундомер с момента старта задачи. */
export function useElapsed(startedAt: number | null | undefined, running: boolean) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    if (!running) return;
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, [running]);
  return startedAt ? Math.max(0, now - startedAt) : 0;
}

const offset = (ms: number) => {
  const s = Math.max(0, Math.round(ms / 1000));
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;
};

function EventDetail({ e }: { e: JobEvent }) {
  const d = (e.detail ?? {}) as Record<string, any>;
  if (e.kind === "search" && Array.isArray(d.results) && d.results.length) {
    return (
      <ul className="feed__results">
        {d.results.map((r: { title: string; url: string }) => (
          <li key={r.url}>
            <a href={r.url} target="_blank" rel="noreferrer">
              {r.title}
            </a>
            <span className="feed__domain">{domainOf(r.url)}</span>
          </li>
        ))}
      </ul>
    );
  }
  if (e.kind === "fetch") {
    return (
      <div className="feed__detail">
        {d.prompt && <p className="muted small">Что искал на странице: {d.prompt}</p>}
        {d.summary && <p className="feed__quote">{d.summary}</p>}
        {d.error && <p className="feed__err">{d.error}</p>}
      </div>
    );
  }
  if ((e.kind === "thinking" || e.kind === "text") && d.text && d.text !== e.title) {
    return <p className="feed__quote">{d.text}</p>;
  }
  if (e.kind === "image" && d.url) {
    return (
      <div className="feed__detail">
        <img className="feed__thumb" src={d.url} alt="" />
        {d.prompt && <p className="muted small">{d.prompt}</p>}
      </div>
    );
  }
  if (e.kind === "image" && d.prompt) return <p className="muted small feed__detail">{d.prompt}</p>;
  if (e.kind === "error" && d.stack) return <pre className="feed__stack">{String(d.stack).split("\n").slice(0, 6).join("\n")}</pre>;
  return null;
}

function hasDetail(e: JobEvent) {
  const d = (e.detail ?? {}) as Record<string, any>;
  return (
    (e.kind === "search" && Array.isArray(d.results) && d.results.length > 0) ||
    (e.kind === "fetch" && (d.summary || d.error || d.prompt)) ||
    ((e.kind === "thinking" || e.kind === "text") && d.text && d.text !== e.title) ||
    (e.kind === "image" && (d.url || d.prompt)) ||
    (e.kind === "error" && d.stack)
  );
}

export function FeedLine({ e, startedAt }: { e: JobEvent; startedAt: number }) {
  const [open, setOpen] = useState(false);
  const Icon = ICON[e.kind] ?? CircleDot;
  const d = (e.detail ?? {}) as Record<string, any>;
  const expandable = hasDetail(e);
  const failed = e.kind === "error" || d.ok === false;
  const title =
    e.kind === "fetch" && d.url ? (
      <a href={d.url} target="_blank" rel="noreferrer" className="feed__link">
        {e.title}
      </a>
    ) : (
      e.title
    );
  return (
    <li className={`feed__item feed__item--${e.kind} ${failed ? "is-failed" : ""}`}>
      <span className="feed__icon" aria-hidden="true">
        <Icon size={15} strokeWidth={2} />
      </span>
      <div className="feed__body">
        <div className="feed__row">
          <span className="feed__title">{title}</span>
          {expandable && (
            <button
              className={`feed__more ${open ? "is-open" : ""}`}
              onClick={() => setOpen((x) => !x)}
              aria-expanded={open}
              aria-label={open ? "Скрыть подробности" : "Подробнее"}
            >
              <ChevronDown size={15} />
            </button>
          )}
          <time className="feed__time">{offset(e.ts - startedAt)}</time>
        </div>
        {open && <EventDetail e={e} />}
      </div>
    </li>
  );
}

/** Полная лента задачи. */
export function JobFeed({ job, events, compact = false }: { job: JobSummary | null; events: JobEvent[]; compact?: boolean }) {
  const running = job?.status === "running";
  const elapsed = useElapsed(job?.started_at, running);
  if (!job) {
    return (
      <div className="feed feed--loading">
        <EcgLoader label="Подключаюсь к Claude" />
      </div>
    );
  }
  return (
    <div className={`feed ${compact ? "feed--compact" : ""}`}>
      {running && (
        <div className="feed__head">
          <EcgLoader size={64} label="Claude работает" />
          <span className="feed__elapsed">{duration(elapsed)}</span>
        </div>
      )}
      {events.length === 0 && running && <p className="muted small feed__empty">Запускаю…</p>}
      <ol className="feed__list" aria-live="polite">
        {events.map((e) => (
          <FeedLine key={e.id} e={e} startedAt={job.started_at} />
        ))}
      </ol>
    </div>
  );
}

/** Свёрнутая строка: текущий шаг + раскрытие всей ленты (для чата редактора). */
export function LiveLine({ job, events, label = "Claude пишет пост…" }: { job: JobSummary | null; events: JobEvent[]; label?: string }) {
  const [open, setOpen] = useState(false);
  const running = !job || job.status === "running";
  const elapsed = useElapsed(job?.started_at, running);
  const visible = events.filter((e) => e.kind !== "done");
  const last = visible.at(-1);
  return (
    <div className="liveline">
      <div className="liveline__top">
        <EcgLoader size={52} label={label} />
        {job && <span className="liveline__time">{duration(elapsed)}</span>}
      </div>
      {last && (
        <button className="liveline__current" onClick={() => setOpen((x) => !x)} aria-expanded={open}>
          <span className="liveline__step">{last.title}</span>
          {visible.length > 1 && <span className="liveline__count">{open ? "Свернуть" : `Все шаги: ${visible.length}`}</span>}
        </button>
      )}
      {open && job && (
        <ol className="feed__list liveline__list">
          {visible.map((e) => (
            <FeedLine key={e.id} e={e} startedAt={job.started_at} />
          ))}
        </ol>
      )}
    </div>
  );
}
